import { GridState } from '@dmc/ui/tables';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { useAppSelector } from '@casops/store/hooks';
import { selectGridData } from '@casops/store/slices/gridSlice';
import { VdsButton, VdsIcon } from '@volt/volt-extended-components-react';
import { ReactElement, useCallback, useMemo } from 'react';

interface GridActionButtonProps {
  sliceKey: string;
  permission: Permission;
  onClick: (ids: number[]) => void;
  disabled?: boolean;
  icon?: string;
  label: string;
}

export const GridActionButton = ({
  sliceKey,
  permission,
  onClick,
  disabled = false,
  icon = 'arrow-up',
  label,
}: GridActionButtonProps): ReactElement | null => {
  const isShow = useIsUserWithPermission(permission);

  // Enforce undefined type as a possibility.
  // RTK entity adapters incorrectly assert that the state is always defined for any ID that is provided.
  const gridState = useAppSelector(state => selectGridData(state, sliceKey)) as
    | GridState
    | undefined;

  const selectedRows = useMemo<string[]>(
    () => gridState?.selectedRows ?? [],
    [gridState?.selectedRows],
  );

  // Force disable if there are no selected rows.
  const isDisabled = disabled || selectedRows.length === 0;

  const handleOnClick = useCallback(() => {
    if (selectedRows.length) {
      // Convert string row identifiers to number ids.
      // Do the conversion at the point of export to avoid type conversion with every selection change.
      const ids = selectedRows.map(Number);
      onClick(ids);
    }
  }, [selectedRows, onClick]);

  return isShow ? (
    <VdsButton
      variant="primary"
      size="medium"
      isFullWidth={false}
      onClick={handleOnClick}
      disabled={isDisabled}
    >
      {label}
      <VdsIcon slot="icon-left" svgIcon={icon} />
    </VdsButton>
  ) : null;
};

export default GridActionButton;
